import React, { useState, useEffect, useCallback, useRef } from 'react'; // Import useRef

const ChevronLeftIcon = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <polyline points="15 18 9 12 15 6"></polyline>
  </svg>
);

const ChevronRightIcon = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <polyline points="9 18 15 12 9 6"></polyline>
  </svg>
);

const PolaroidImageSlider = () => {
  const [images, setImages] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [touchStartX, setTouchStartX] = useState(0); // State for touch start X coordinate
  const [showNavButtons, setShowNavButtons] = useState(true); // State to control nav button visibility

  const sliderRef = useRef(null); // Ref for attaching touch event listeners

  // Function to check screen width and update showNavButtons state
  const checkScreenWidth = useCallback(() => {
    setShowNavButtons(window.innerWidth >= 868);
  }, []);

  useEffect(() => {
    // Initial check on mount
    checkScreenWidth();
    // Add event listener for window resize
    window.addEventListener('resize', checkScreenWidth);
    // Cleanup event listener on component unmount
    return () => window.removeEventListener('resize', checkScreenWidth);
  }, [checkScreenWidth]); // Dependency array includes checkScreenWidth

  useEffect(() => {
    const fetchImages = async () => {
      if (isFetching || images.length > 0) return;
      setIsFetching(true);
      setLoading(true);

      try {
        const response = await fetch('https://ethiccraft.org/backend/camp_memories/read_isShown.php');
        const data = await response.json(); // Expected: [{ id, src, caption }]
        
        // Filter out items with missing or invalid image paths
        const validImages = data.filter(item => item.image && typeof item.image === 'string');

        setImages(validImages);
        if (validImages.length > 0) {
          setCurrentIndex(Math.floor(validImages.length / 2));
        }
      } catch (error) {
        console.error("Error fetching images:", error);
        setImages([]);
      }

      setLoading(false);
      setIsFetching(false);
    };

    fetchImages();
  }, [isFetching, images.length]);

  const nextSlide = useCallback(() => {
    if (images.length === 0) return;
    setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
  }, [images.length]);

  const prevSlide = useCallback(() => {
    if (images.length === 0) return;
    setCurrentIndex((prevIndex) => (prevIndex - 1 + images.length) % images.length);
  }, [images.length]);

  // Touch event handlers
  const handleTouchStart = useCallback((e) => {
    setTouchStartX(e.touches[0].clientX);
  }, []);

  const handleTouchMove = useCallback((e) => {
    // Prevent default touch behavior (e.g., scrolling) only if it's primarily horizontal
    // This is a subtle optimization: avoid preventing all scrolling on touch
    if (Math.abs(e.touches[0].clientX - touchStartX) > Math.abs(e.touches[0].clientY - e.touches[0].clientY)) {
        e.preventDefault();
    }
  }, [touchStartX]);

  const handleTouchEnd = useCallback((e) => {
    const touchEndX = e.changedTouches[0].clientX;
    const deltaX = touchStartX - touchEndX;
    const swipeThreshold = 50; // Minimum horizontal swipe distance to trigger a slide change

    if (deltaX > swipeThreshold) {
      nextSlide(); // Swipe left, go to next slide
    } else if (deltaX < -swipeThreshold) {
      prevSlide(); // Swipe right, go to previous slide
    }
  }, [touchStartX, nextSlide, prevSlide]);

  const headingText = "Camp-Memories";
  const animatedHeading = headingText.split("").map((char, index) => (
    <span key={index} style={{ animationDelay: `${index * 0.07 + 0.4}s` }}>
      {char === " " ? "\u00A0" : char}
    </span>
  ));

  const getSlideStyle = (index) => {
    if (images.length === 0) return {};

    const offset = index - currentIndex;
    const totalSlides = images.length;
    const isCurrent = offset === 0;

    let transform = '';
    let zIndex = totalSlides - Math.abs(offset);
    let opacity = 0;

    const visibleRange = 2;

    if (Math.abs(offset) <= visibleRange) {
      opacity = 1;
      let translateX, rotateDeg, scale;

      if (isCurrent) {
        translateX = 0;
        rotateDeg = 0;
        scale = 1;
        zIndex = totalSlides + 1;
      } else {
        const sideFactor = offset > 0 ? 1 : -1;
        const absOffset = Math.abs(offset);
        translateX = sideFactor * (absOffset * 20 + (absOffset === 1 ? 5 : 15));
        rotateDeg = sideFactor * (absOffset * 5 + (absOffset === 1 ? -2 : 3) * sideFactor * (index % 2 === 0 ? 1 : -0.5));
        scale = 1 - absOffset * 0.12;
        const verticalOffset = (absOffset === 1 ? -2 : -1) * (index % 3);
        transform = `translateY(${verticalOffset}%) `;
      }
      transform += `translateX(${translateX}%) rotate(${rotateDeg}deg) scale(${scale})`;
    } else {
      opacity = 0;
      const farSideFactor = offset > 0 ? 1 : -1;
      transform = `translateX(${farSideFactor * 100}%) rotate(${farSideFactor * 25}deg) scale(0.5)`;
      zIndex = 0;
    }

    return {
      transform,
      zIndex,
      opacity,
    };
  };

  if (loading && images.length === 0) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p className="loading-text font-permanent-marker">Loading Memories...</p>
      </div>
    );
  }

  return (
    <div className="polaroid-slider-component-container">
      <h1 className="polaroid-slider-heading animate-letter-bounce-in heading-shine-effect">
        {animatedHeading}
      </h1>

      {images.length > 0 ? (
        <div
          className="image-slider-container11"
          ref={sliderRef} // Attach ref to the slider container
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {images.map((image, index) => (
            <div
              key={image.id}
              className={`polaroid-card ${index === currentIndex ? 'is-current' : ''}`}
              style={getSlideStyle(index)}
              onClick={() => setCurrentIndex(index)}
            >
              <div className="polaroid-image-wrapper">
                <img
                  src={`https://ethiccraft.org/${image.image}`}
                  className="polaroid-image"
                  alt='Camp Memory'
                />
              </div>
              <div className="polaroid-caption-area">
                <p className="polaroid-caption-text">
                  {image.caption}
                </p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        !loading && <p className="no-images-message">No memories to share yet. Check back soon!</p>
      )}

      {images.length > 1 && showNavButtons && ( // Conditionally render based on showNavButtons state
        <div className="navigation-buttons-container">
          <button
            onClick={prevSlide}
            aria-label="Previous Memory"
            className="navigation-button"
          >
            <ChevronLeftIcon />
          </button>
          <button
            onClick={nextSlide}
            aria-label="Next Memory"
            className="navigation-button"
          >
            <ChevronRightIcon />
          </button>
        </div>
      )}
    </div>
  );
};

export default PolaroidImageSlider;